import React from 'react';
import { t } from '../../i18n';
import Spinner from './Spinner';

export interface DialogActionButton {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Bootstrap button variant class, e.g. 'btn-primary', 'btn-danger'. Defaults to 'btn-primary'. */
  variant?: string;
}

interface DialogProps {
  title: string;
  children?: React.ReactNode;
  /** If provided, renders a "キャンセル" secondary button that calls this handler. */
  onCancel?: () => void;
  /** If provided, renders a "閉じる" secondary button that calls this handler. */
  onClose?: () => void;
  /** Primary action buttons rendered before the cancel/close button. */
  actionButtons?: DialogActionButton[];
  /** When true, replaces dialog content with a centered spinner. */
  loading?: boolean;
  /** Additional CSS class applied to modal-dialog element. */
  dialogClassName?: string;
  /** Called when the backdrop is clicked. Defaults to onClose ?? onCancel. */
  onBackdropClick?: () => void;
}

const Dialog: React.FC<DialogProps> = ({
  title,
  children,
  onCancel,
  onClose,
  actionButtons,
  loading = false,
  dialogClassName,
  onBackdropClick,
}) => {
  const backdropHandler = onBackdropClick ?? onClose ?? onCancel;
  const hasFooter =
    !loading && (!!onCancel || !!onClose || (actionButtons && actionButtons.length > 0));

  return (
    <>
      <div className="modal-backdrop show" onClick={backdropHandler} />
      <div className="modal d-block" tabIndex={-1} role="dialog">
        <div
          className={`modal-dialog modal-dialog-centered app-dialog${dialogClassName ? ` ${dialogClassName}` : ''}`}
          role="document"
        >
          <div className="modal-content">
            {loading ? (
              <div className="modal-body app-dialog__loading-body">
                <Spinner />
              </div>
            ) : (
              <>
                <div className="modal-header">
                  <h6 className="modal-title">{title}</h6>
                </div>
                {children && <div className="modal-body">{children}</div>}
                {hasFooter && (
                  <div className="modal-footer">
                    {actionButtons?.map((btn, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`btn ${btn.variant ?? 'btn-primary'}`}
                        onClick={btn.onClick}
                        disabled={btn.disabled}
                      >
                        {btn.label}
                      </button>
                    ))}
                    {onCancel && (
                      <button type="button" className="btn btn-secondary" onClick={onCancel}>
                        {t('dialog_cancel_button')}
                      </button>
                    )}
                    {onClose && (
                      <button type="button" className="btn btn-secondary" onClick={onClose}>
                        {t('dialog_close_button')}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Dialog;
